import React from 'react';
import { Link } from 'react-router-dom';
import BlogPostLayout from './BlogPostLayout';

const ScreenTenantsKenya = () => (
  <BlogPostLayout
    title="How to Screen Tenants in Kenya: A Complete Guide for Landlords"
    published="27 June 2026"
  >
    <p>
      Finding the right tenant is one of the biggest challenges landlords face in Kenya. A bad tenant
      can lead to rent arrears, property damage, disputes, and costly evictions. Proper tenant screening
      helps landlords reduce risk and protect their investment.
    </p>

    <h2>Why Tenant Screening Matters</h2>
    <p>
      Many landlords only look at a tenant&apos;s current income before renting out a property. However,
      income alone does not reveal whether the tenant has a history of paying rent on time, respecting
      lease agreements, or maintaining rental properties.
    </p>
    <p>Tenant screening helps landlords:</p>
    <ul>
      <li>Reduce the risk of rent defaults.</li>
      <li>Identify tenants with a positive rental history.</li>
      <li>Verify identity and prevent fraud.</li>
      <li>Protect property from damage.</li>
      <li>Save time and money on disputes.</li>
    </ul>

    <h2>What Every Landlord Should Check Before Renting</h2>

    <h3>1. Verify the Tenant&apos;s Identity</h3>
    <p>
      Always confirm the tenant&apos;s identity using a valid national ID or passport. This helps ensure
      the information provided is genuine and reduces the risk of fraud.
    </p>

    <h3>2. Check Employment and Income</h3>
    <p>
      Request proof of employment or business income. Understanding a tenant&apos;s financial situation
      can help determine their ability to pay rent consistently.
    </p>

    <h3>3. Review Rental History</h3>
    <p>
      Past behavior is often the best predictor of future behavior. Ask about previous residences and
      rental experiences.
    </p>
    <p>Questions to consider:</p>
    <ul>
      <li>Did the tenant pay rent on time?</li>
      <li>Were there any complaints from neighbors?</li>
      <li>Did they leave the property in good condition?</li>
    </ul>

    <h3>4. Check Rent Payment Behavior</h3>
    <p>
      A tenant may have a stable income but still have a history of late payments. Reviewing rent payment
      behavior can help landlords make informed decisions.
    </p>

    <h3>5. Conduct Background Verification</h3>
    <p>
      Verify the information provided by the applicant, including contact details, employment records,
      and references.
    </p>

    <h2>Common Mistakes Landlords Make</h2>
    <p>Many landlords:</p>
    <ul>
      <li>Skip tenant verification.</li>
      <li>Rely solely on personal referrals.</li>
      <li>Fail to check rental history.</li>
      <li>Ignore warning signs during the application process.</li>
    </ul>
    <p>These mistakes can lead to long-term financial losses.</p>

    <h2>How TenantScore Helps Landlords in Kenya</h2>
    <p>
      TenantScore is a unified platform designed to help landlords, property managers, agents, and BnB
      hosts make informed decisions before renting out their properties.
    </p>
    <p>With TenantScore, users can:</p>
    <ul>
      <li>Verify tenant and guest identities.</li>
      <li>Track rental history across properties.</li>
      <li>Review rent payment behavior.</li>
      <li>Manage rental properties from one dashboard.</li>
      <li>Market vacant units to potential tenants.</li>
      <li>Help tenants find homes through Google Maps-powered property search.</li>
    </ul>
    <p>
      By using reliable rental records and verification tools, landlords can reduce risk and rent with
      greater confidence.
    </p>

    <h2>Final Thoughts</h2>
    <p>
      Tenant screening is no longer optional in today&apos;s rental market. Taking time to verify tenants,
      review rental history, and assess payment behavior can save landlords significant stress and
      financial loss.
    </p>
    <p>
      Whether you own one rental unit or manage multiple properties, a structured screening process helps
      ensure you attract responsible tenants and protect your investment.
    </p>
    <p>
      Start screening smarter with{' '}
      <Link to="/signup">TenantScore</Link> and make informed rental decisions backed by verified rental
      data.
    </p>
  </BlogPostLayout>
);

export default ScreenTenantsKenya;
